/**
 * Format farmer/user address for display.
 * Uses the address from the user's profile settings (Location section in farmer profile).
 * Handles both nested address object (User model) and flattened fields.
 * @param {Object} user - User/farmer object with address (from profile)
 * @returns {string|null} Formatted address string or null if empty
 */
export const formatFarmerAddress = (user) => {
  if (!user) return null;
  const addr = user.address;
  const parts = [
    addr?.street,
    addr?.municipality || addr?.city,
    addr?.district,
    addr?.province
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : null;
};

/**
 * Format short location (district, province) for compact display.
 * Uses the address from the user's profile settings (Location section in farmer profile).
 * Falls back to city/municipality if district/province are not set.
 * @param {Object} user - User/farmer object with address (from profile)
 * @returns {string} Formatted location string, never empty
 */
export const formatFarmerLocation = (user) => {
  if (!user || typeof user !== 'object') return 'N/A';
  const addr = user.address;
  if (!addr || typeof addr !== 'object') {
    return (user.district || user.province || '').trim() || 'N/A';
  }
  const district = (addr.district || user.district || '').trim();
  const province = (addr.province || user.province || '').trim();
  let parts = [district, province].filter(Boolean);
  if (parts.length === 0) {
    parts = [addr.city, addr.municipality, addr.street].map(s => (s || '').trim()).filter(Boolean);
  }
  return parts.length > 0 ? parts.join(', ') : 'N/A';
};

/**
 * Format only the street/address for display (no district, province).
 * Used in consumer orders for farmer address.
 * @param {Object} user - User/farmer object with address (from profile)
 * @returns {string} Street/address string or 'N/A'
 */
export const formatFarmerStreetAddress = (user) => {
  if (!user || typeof user !== 'object') return 'N/A';
  const addr = user.address;
  if (!addr || typeof addr !== 'object') return 'N/A';
  const street = (addr.street || '').trim();
  return street || 'N/A';
};
